import { useState } from 'react';

/**
 * 검색 입력 필드 기능을 관리하는 커스텀 훅
 *
 * 주요 기능:
 * 1. 입력값 상태 관리 (URL 검색어로 초기화·동기화)
 * 2. 엔터 키 이벤트 처리를 통한 검색 실행
 * 3. 검색 버튼 클릭 핸들러
 *
 * Panel 이름 → 유전자 목록 변환은 URL 을 source of truth 로 삼는
 * useGeneSearchManager 의 파생 로직이 담당하므로, 여기서는 원본 입력값만 넘긴다.
 *
 * @param {Function} onSearch - 검색 실행 시 호출될 콜백 함수 (원본 입력값을 인자로 받음)
 * @param {string} initialInputValue - URL 에서 온 초기 검색어
 * @returns {Object} 검색 입력 관련 상태와 함수
 * @returns {string} inputValue - 현재 입력된 검색어
 * @returns {Function} setInputValue - 입력값 설정 함수
 * @returns {Function} handleKeyDown - 엔터 키 이벤트 핸들러
 * @returns {Function} handleSearchClick - 검색 버튼 클릭 핸들러
 */
export function useInputField(
  onSearch: (originalInput: string) => void,
  initialInputValue: string = '',
) {
  const [inputValue, setInputValue] = useState(initialInputValue);
  // 뒤로/앞으로 가기처럼 컴포넌트가 유지된 채 URL만 바뀌는(soft nav) 경우,
  // useState 초기값은 이미 지나갔으므로 URL 검색어에 입력값을 맞춰 재동기화한다.
  const [prevInitialValue, setPrevInitialValue] = useState(initialInputValue);
  if (initialInputValue !== prevInitialValue) {
    setPrevInitialValue(initialInputValue);
    setInputValue(initialInputValue);
  }

  // 엔터 키 이벤트 핸들러
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch(inputValue);
    }
  };

  // 검색 버튼 클릭 핸들러
  const handleSearchClick = () => {
    onSearch(inputValue);
  };

  return {
    inputValue,
    setInputValue,
    handleKeyDown,
    handleSearchClick,
  };
}
